// @ts-strict-ignore
import { tokenize } from '~/flow/expressionHelpers';
import { ActionType } from '../../constants';
import type {
  Action,
  Expression,
  LegacyAction,
  LegacyActionOption,
  LegacyDataRoute,
  LegacyMatrixAxisDataRoutes,
  LegacyMatrixModelResponse,
  LegacyThreshold,
  ObjectIdReverseTransformationMap,
  Outcome,
  Tag,
} from '../../types';
import { remapExpressionDataRouteIds } from '../../utils';
import reverseTransformBooleanRules from './rules';
import {
  reverseTransformExitTypeAction,
  reverseTransformImportedDataPassingTypeAction,
  reverseTransformInputOutputTypeAction,
  reverseTransformMatrixModelTypeAction,
  reverseTransformOutcomeTypeAction,
  reverseTransformRunTypeAction,
  reverseTransformTagTypeAction,
  reverseTransformWorkflowTypeAction,
} from './actions';
import { reverseTransformJsonThresholdsToLegacyThresholds } from './thresholds';
import { reverseTransformJsonDataRoutesToLegacyDataRoutes } from './dataRoutes';

function getMatrixAxisData({
  actions,
  dataRoutes,
  matrixModels,
}: {
  actions: LegacyAction[];
  dataRoutes: LegacyDataRoute[];
  matrixModels: LegacyMatrixModelResponse[];
}): Record<string, LegacyMatrixAxisDataRoutes> {
  const matrixModelsAxisObject = {};
  matrixModels.forEach(matrixModel => {
    const matrixModelActionOptions = actions.find(action => action.id === matrixModel.actionId)
      ?.options as Record<string, LegacyActionOption>;

    const matrixModelAxisX = dataRoutes.find(dataRoute => dataRoute.id
      === matrixModelActionOptions.data_route_x?.value);
    const matrixModelAxisY = dataRoutes.find(dataRoute => dataRoute.id
      === matrixModelActionOptions.data_route_y.value);
    matrixModelsAxisObject[matrixModel.actionId] = {
      x: matrixModelAxisX,
      y: matrixModelAxisY,
    };
  });

  return matrixModelsAxisObject;
}

function reverseTransformJsonActionsToLegacyActions({
  actions, tags, services, outcomes,
}: {
  actions: Action[];
  tags: Tag[];
  services: Record<number, Record<
  string, string | number | object | null
  >>;
  outcomes: Outcome[];
}) {
  const transformedActionsToLegacyActionsIdMap = {};
  let transformedActionOptionIndexNumber = 0;
  const transformedMatrixModels: LegacyMatrixModelResponse[] = [];

  const transformedLegacyActions = Object.entries(
    actions,
  ).map(([actionKey, actionValue], index) => {
    /**
     * The `run` option was deliberately removed from the `ActionTypes` enum because it gets
     * rewritten to `service` in the new schema. The only place `run` should be referenced
     * is in the code to transform or reverse between the new and old schema.
     */
    let actionType: ActionType | 'run' = actionValue.type;

    if (actionValue.type === ActionType.SERVICE) {
      actionType = 'run';
    }

    let transformedLegacyAction: LegacyAction = {
      id: index + 1,
      type: actionType,
      publicId: actionKey,
      options: {},
      name: '',
      tagColor: '',
      customerTagId: null,
      applicationId: null,
      booleanRule: '',
      thresholdsRule: '',
      customerOutcomeId: null,
      precedence: null,
      publishedAttributeToken: null,
      serviceFailureDefault: null,
      serviceId: null,
      credentialOwner: null,
    };
    let transformedData;

    const actionTypeNotInputOutputOutcomeAndImportedTypes = ![
      ActionType.INPUT as string,
      ActionType.OUTPUT as string,
      ActionType.OUTCOME_REASON as string,
      ActionType.IMPORTED_MATRIX_MODEL as string,
      ActionType.IMPORTED_OUTPUT_ATTRIBUTE as string,
      ActionType.WORKFLOW as string,
    ].includes(actionType);

    transformedActionsToLegacyActionsIdMap[actionKey] = index + 1;
    const actionOptions = actionValue.options
      ? Object.entries(actionValue.options)
      : [];
    if (actionOptions.length > 0 && actionTypeNotInputOutputOutcomeAndImportedTypes) {
      actionOptions.forEach(([
        actionOptionKey, actionOptionValue,
      ]) => {
        transformedLegacyAction.options[actionOptionKey] = {
          publicId: actionOptionValue.publicId,
          actionId: transformedLegacyAction.id,
          id: transformedActionOptionIndexNumber += 1,
          key: actionOptionKey,
          value: String(actionOptionValue.value),
          jsonValue: actionOptionValue.jsonValue,
          serviceConfigOptionId: actionOptionValue.serviceConfigOptionId,
          selectedValues: actionOptionValue.selectedValues,
        };
      });
    }

    if (actionType === ActionType.TAG) {
      transformedData = reverseTransformTagTypeAction({
        action: actionValue,
        tags,
      });
    }

    if (actionType === 'run') {
      transformedData = reverseTransformRunTypeAction({
        action: actionValue,
        services,
      });
    }

    if (actionType === ActionType.OUTCOME) {
      transformedData = reverseTransformOutcomeTypeAction({
        action: actionValue,
        outcomes,
      });
    }

    if (actionTypeNotInputOutputOutcomeAndImportedTypes && transformedData) {
      transformedData.options = transformedLegacyAction.options;
    }

    if (actionType === ActionType.INPUT || actionType === ActionType.OUTPUT) {
      const {
        actionData,
        actionOptionData,
      } = reverseTransformInputOutputTypeAction({
        action: actionValue,
        actionIndex: transformedLegacyAction.id,
        transformedActionOptionIndexNumber,
      });

      transformedData = {
        ...actionData,
        options: actionOptionData,
      };

      transformedActionOptionIndexNumber = actionType === 'input'
        ? transformedActionOptionIndexNumber + 3
        : transformedActionOptionIndexNumber + 5;
    }

    if (actionType === ActionType.MATRIX_MODEL) {
      const {
        actionData,
        actionOptionData,
        matrixData,
      } = reverseTransformMatrixModelTypeAction({
        action: actionValue,
        actionIndex: transformedLegacyAction.id,
        transformedActionOptionIndexNumber,
      });

      transformedMatrixModels.push(matrixData);

      transformedData = {
        ...actionData,
        options: actionOptionData,
      };

      transformedActionOptionIndexNumber += Object.values(actionOptionData).length;
    }

    if (actionType === ActionType.OUTCOME_REASON
      || actionType === ActionType.IMPORTED_OUTPUT_ATTRIBUTE
      || actionType === ActionType.IMPORTED_MATRIX_MODEL) {
      const {
        actionData,
        actionOptionData,
      } = reverseTransformImportedDataPassingTypeAction({
        action: actionValue,
        actionIndex: transformedLegacyAction.id,
        transformedActionOptionIndexNumber,
      });

      transformedData = {
        ...actionData,
        options: actionOptionData,
      };

      transformedActionOptionIndexNumber += 4;
    }

    if (actionType === ActionType.WORKFLOW) {
      transformedData = reverseTransformWorkflowTypeAction({
        workflowAction: actionValue,
      });
    }

    if (actionType === ActionType.EXIT) {
      transformedData = reverseTransformExitTypeAction({
        exitAction: actionValue,
      });
    }

    transformedLegacyAction = {
      ...transformedLegacyAction,
      ...transformedData,
    };

    return transformedLegacyAction;
  });

  return {
    transformedLegacyActionsWithoutRules: transformedLegacyActions,
    transformedActionsToLegacyActionsIdMap,
    transformedMatrixModels,
  };
}

function reverseTransformBooleanRuleAndDataRoutesInActions({
  jsonActions,
  transformedLegacyActionsWithoutRules,
  transformedThresholdsToLegacyThresholdsIdMap,
  transformedDataRoutesToLegacyDataRoutesIdMap,
}: {
  jsonActions: Record<string, Action>;
  transformedLegacyActionsWithoutRules: LegacyAction[];
  transformedThresholdsToLegacyThresholdsIdMap: ObjectIdReverseTransformationMap;
  transformedDataRoutesToLegacyDataRoutesIdMap: ObjectIdReverseTransformationMap;
}) {
  let actionOptionsWithExpressions = [];
  const transformedActionsWithRules = transformedLegacyActionsWithoutRules.map(
    action => {
      const options = action.options as Record<string, LegacyActionOption>;

      if (action.type === ActionType.OUTPUT && options.expression) {
        const remappedJsonValue = remapExpressionDataRouteIds({
          expressionArray: options.expression.jsonValue as Expression,
          dataRouteIdMap: transformedDataRoutesToLegacyDataRoutesIdMap,
        });

        const tokenizedExpressionValue = tokenize(remappedJsonValue);
        options.expression.value = JSON.stringify(tokenizedExpressionValue);
        options.expression.jsonValue = tokenizedExpressionValue;
      }

      const jsonActionObject = jsonActions[action.publicId];
      if (jsonActionObject?.rules) {
        action.thresholdsRule = reverseTransformBooleanRules({
          booleanRules: jsonActionObject.rules,
          transformedThresholdsToLegacyThresholdsIdMap,
        });
      }

      if (action.type === ActionType.MATRIX_MODEL) {
        if (options.data_route_x) {
          options.data_route_x.value = transformedDataRoutesToLegacyDataRoutesIdMap[
            options.data_route_x.value as number];
        }
        options.data_route_y.value = transformedDataRoutesToLegacyDataRoutesIdMap[
          options.data_route_y.value as number];
      }

      actionOptionsWithExpressions = [
        ...actionOptionsWithExpressions,
        ...Object.values(options),
      ];

      return action;
    });

  return {
    transformedActionsWithRules,
    transformedActionOptionsWithUpdatedExpressions: actionOptionsWithExpressions,
  };
}

export const reverseTransformSdkConfig = sdkConfig => {
  const transformed = sdkConfig.fields.reduce(
    (acc, field) => {
      acc[field.slug] = field.value;
      return acc;
    },
    { plugin: sdkConfig.plugin },
  );

  // Default (base) values are serialized flat (keyed by slug above). Translated copy is
  // nested by language code so the backend can persist it per-language:
  //   { es: { <slug>: <value> } }
  if (sdkConfig.translations?.length) {
    transformed.translations = sdkConfig.translations.reduce((acc, translation) => {
      acc[translation.languageCode] = acc[translation.languageCode] || {};
      acc[translation.languageCode][translation.slug] = translation.value;
      return acc;
    }, {});
  }

  return transformed;
};

export default function reverseTransformJsonToLegacyRepresentation({
  actions,
  thresholds,
  dataRoutes,
  tags,
  services,
  outcomes,
  sdkConfig,
}) {
  const {
    transformedLegacyActionsWithoutRules,
    transformedActionsToLegacyActionsIdMap,
    transformedMatrixModels,
  }: {
    transformedLegacyActionsWithoutRules: LegacyAction[];
    transformedActionsToLegacyActionsIdMap: ObjectIdReverseTransformationMap;
    transformedMatrixModels: LegacyMatrixModelResponse[];
  } = reverseTransformJsonActionsToLegacyActions({
    actions, tags, services, outcomes,
  });

  const {
    transformedLegacyThresholds,
    transformedThresholdsToLegacyThresholdsIdMap,
  }: {
    transformedLegacyThresholds: LegacyThreshold[];
    transformedThresholdsToLegacyThresholdsIdMap: ObjectIdReverseTransformationMap;
  } = reverseTransformJsonThresholdsToLegacyThresholds({
    thresholds,
    transformedActionsToLegacyActionsIdMap,
    jsonActions: actions,
  });

  const {
    transformedLegacyDataRoutes,
    transformedDataRoutesToLegacyDataRoutesIdMap,
  }: {
    transformedLegacyDataRoutes: LegacyDataRoute[];
    transformedDataRoutesToLegacyDataRoutesIdMap: ObjectIdReverseTransformationMap;
  } = reverseTransformJsonDataRoutesToLegacyDataRoutes({
    dataRoutes,
    actions,
    transformedActionsToLegacyActionsIdMap,
  });

  const {
    transformedActionsWithRules,
    transformedActionOptionsWithUpdatedExpressions,
  }: {
    transformedActionsWithRules: LegacyAction[];
    transformedActionOptionsWithUpdatedExpressions: LegacyActionOption[];
  } = reverseTransformBooleanRuleAndDataRoutesInActions({
    jsonActions: actions,
    transformedLegacyActionsWithoutRules: transformedLegacyActionsWithoutRules as LegacyAction[],
    transformedThresholdsToLegacyThresholdsIdMap,
    transformedDataRoutesToLegacyDataRoutesIdMap,
  });

  const matrixAxisData: Record<string, LegacyMatrixAxisDataRoutes> = getMatrixAxisData({
    actions: transformedActionsWithRules,
    dataRoutes: transformedLegacyDataRoutes,
    matrixModels: transformedMatrixModels,
  });

  const transformedSdkConfig = reverseTransformSdkConfig(sdkConfig);

  return {
    actions: transformedActionsWithRules,
    actionOptions: transformedActionOptionsWithUpdatedExpressions,
    thresholds: transformedLegacyThresholds,
    dataRoutes: transformedLegacyDataRoutes,
    matrixModels: transformedMatrixModels,
    matrixAxisData,
    sdkConfig: transformedSdkConfig,
  };
}
